import React, { useRef, useCallback } from 'react';
import type { TableProps } from 'antd';
import { Grid, Table } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Loader } from './Loader';

const scrollConfig = {
  y: 465,
};

interface Props<RecordType extends object> extends TableProps<RecordType> {
  debug?: boolean;
  infinity?: boolean;
  scroll?: { x?: number | string; y?: number | string };
  lastId?: string;
  dataSource: RecordType[];
  loading: boolean;
  fetchNext: () => void;
  hasNext: boolean;
  emptyText?: string | null;
  emptyComponent?: React.ReactNode;
}

export const SmartTable = <
  RecordType extends object = Record<string, unknown>,
>({
  debug = false,
  infinity = false,
  scroll = scrollConfig,
  dataSource = [],
  lastId = '',
  loading = false,
  fetchNext = () => null,
  hasNext = false,
  emptyText = null,
  emptyComponent = null,
  onRow,
  ...props
}: Props<RecordType>) => {
  const { t } = useTranslation('common');
  const isFetchingRef = useRef(false);
  const screens = Grid.useBreakpoint();
  const mobileScrollX = screens.md ? undefined : 'max-content';

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasNext || isFetchingRef.current) return;
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        isFetchingRef.current = true;
        fetchNext();
        setTimeout(() => {
          isFetchingRef.current = false;
        }, 500);
      }
    },
    [hasNext, fetchNext]
  );

  const renderEmpty = useCallback(() => {
    if (emptyComponent) {
      return emptyComponent;
    }
    return (
      <div style={{ textAlign: 'center' }}>
        <FileSearchOutlined style={{ fontSize: 20 }} />
        <p style={{ color: '#535353' }}>{emptyText || t('No data found')}</p>
      </div>
    );
  }, [t, emptyText, emptyComponent]);

  if (!infinity) {
    return (
      <Loader loading={loading}>
        <Table
          onRow={onRow}
          locale={{ emptyText: renderEmpty() }}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: mobileScrollX }}
          {...props}
        />
      </Loader>
    );
  }

  return (
    <Loader loading={loading}>
      <Table
        onRow={onRow}
        dataSource={dataSource}
        scroll={{ x: mobileScrollX, ...scroll }}
        pagination={false}
        locale={{ emptyText: renderEmpty() }}
        onScroll={handleScroll}
        {...props}
      />
    </Loader>
  );
};
